<template>
  <SloLoader :loading="paymetricAccessInfoLoading" size="xl">
    <article class="personal-details">
      <div class="pt-md px-md pb-0 sm:px-lg sm:pt-lg">
        <h3 id="payment-detail-form-header" class="text-xl fw-strong">
          {{ 'Add New Payment Method' | t }}
        </h3>
      </div>
      <form @submit.prevent="handleSubmit" class="mt-lg">
        <div class="flex flex-col px-md sm:px-lg">
          <div>
            <div class="paymetric-container">
              <iframe
                :src="paymetricAccessInfo.paymetricsIframeAccessTokenUrl"
                id="paymetric-iframe"
                name="paymetric-iframe"
                width="100%"
                height="100%"
                frameborder="0"
              ></iframe>
            </div>
          </div>
          <SloField class="self-start mt-md">
            <SloLabel class="flex items-center justify-start">
              <SloInput
                v-model="newPaymentDetailIsDefault"
                :checked="newPaymentDetailIsDefault"
                type="checkbox"
                :value="true"
                class="mr-sm"
              />
              {{ 'Make Default' | t }}
            </SloLabel>
          </SloField>
          <div class="my-lg">
            <h3 class="text-xl fw-strong mt-0 mb-md">{{ 'Billing Address' | t }}</h3>
            <AddressFormFields
              :original-address="address"
              :countries="billingCountries"
              :regions="billingCountryRegions"
            />
          </div>
        </div>
        <div class="personal-details--footer flex justify-end gap-md px-md py-md bg-gray-100 sm:px-lg">
          <SloButton
            :disabled="loading"
            :to="{name: 'marketplace-account-payments'}"
            fill="outline"
          >
            {{ 'Cancel' | t }}
          </SloButton>
          <SloButton
            :disabled="loading"
            type="submit"
            fill="solid"
            class="fixed-button-width"
          >
            <SloLoader :loading="loading" size="md" color="white">
              {{ 'Add' | t }}
            </SloLoader>
          </SloButton>
        </div>
      </form>
    </article>
  </SloLoader>
</template>

<script>
import t from '../../main/i18n';
import {handleApiErrors} from '../../main/utils';
import {marketplaceApi} from '../apis/marketplace';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import AddressFormFields from '../components/AddressFormFields.vue';

export default {
  components: {
    SloLoader,
    SloButton,
    SloField,
    SloLabel,
    SloInput,
    AddressFormFields
  },
  filters: {
    t
  },
  data() {
    return {
      loading: false,
      paymetricAccessInfo: {},
      paymetricAccessInfoLoading: true,
      billingCountries: [],
      billingCountryRegions: [],
      newPaymentDetailIsDefault: false,
      address: {
        firstName: '',
        companyName: '',
        country: {isocode: 'US'},
        line1: '',
        line2: '',
        town: '',
        region: {isocode: '', isocodeShort: ''},
        postalCode: ''
      },
      addressErrors: []
    };
  },
  created() {
    const paymetricIframeScript = document.createElement('script');
    paymetricIframeScript.setAttribute(
      'src',
      'https://cert-xiecomm.paymetric.com/diecomm/scripts/XIFrame/XIFrame-1.2.0.js'
    );
    document.head.appendChild(paymetricIframeScript);
  },
  async mounted() {
    await marketplaceApi.getCountryCodes('BILLING')
      .then(async res => {
        this.billingCountries = res.data?.countries;
        this.address.country.isocode = 'US';
        await this.getCountryRegions();
      })
      .catch(() => {
        this.shippingCountries = [];
        this.address.country.isocode = 'US';
      });

    await this.getPaymetricAccessInfo();
  },
  watch: {
    async 'address.country.isocode'(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.getCountryRegions();
      }
    }
  },
  methods: {
    async getPaymetricAccessInfo() {
      this.paymetricAccessInfoLoading = true;
      await marketplaceApi.getPaymetricAccessInfo()
        .then(res => {
          if (!res?.data) {
            this.paymetricAccessInfoLoading = false;
            this.$snotify.error(t('We are unable to process payments at this time'));
            return;
          }
          this.paymetricAccessInfo = res.data;
          this.paymetricAccessInfoLoading = false;
        })
        .catch(() => {
          this.paymetricAccessInfoLoading = false;
          this.$snotify.error(t('We are unable to process payments at this time'));
        });
    },
    async getCountryRegions() {
      this.address.region.isocode = '';
      this.address.region.isocodeShort = '';
      await marketplaceApi.getCountryRegions(this.address.country.isocode)
        .then(res => this.billingCountryRegions = res.data?.regions)
        .catch(() => this.billingCountryRegions = []);
    },
    async validateAddress() {
      if (this.address.country.isocode !== 'US') return true;

      this.loading = true;
      this.addressErrors = [];
      return await marketplaceApi.validateAddressWithStrikeIron(this.address)
        .then(res => {
          if (res.data) {
            // Update address data with validated address from StrikeIron
            this.address.line1 = res.data.line1;
            this.address.line2 = res.data.line2;
            this.address.town = res.data.town;
            this.address.region.isocode = res.data.region.isocode;
            this.address.region.isocodeShort = res.data.region.isocodeShort;
            this.address.postalCode = res.data.postalCode;
            return true;
          } else {
            this.loading = false;
            this.$snotify.clear();
            this.$snotify.error(t('Invalid address provided'));
            return false;
          }
        })
        .catch(e => {
          this.loading = false;
          if (e?.response?.data?.errors) {
            this.addressErrors = e?.response?.data?.errors;
          }
          this.$snotify.clear();
          this.$snotify.error(t('Invalid address provided'));
          return false;
        });
    },
    async handleSubmit() {
      const self = this;
      // eslint-disable-next-line no-undef
      $XIFrame.validate({
        iFrameId: 'paymetric-iframe',
        targetUrl: this.paymetricAccessInfo.paymetricsIframeAccessTokenUrl,
        async onValidate(valid) {
          if (valid) {
            if (!await self.validateAddress()) return;
            await self.submitPaymetricForm();
          } else {
            document.getElementById('payment-detail-form-header').scrollIntoView();
          }
        }
      });
    },
    async submitPaymetricForm() {
      const self = this;
      // eslint-disable-next-line no-undef
      $XIFrame.submit({
        iFrameId: 'paymetric-iframe',
        targetUrl: this.paymetricAccessInfo.paymetricsIframeAccessTokenUrl,
        async onSuccess(msg) {
          const message = JSON.parse(msg);

          if (message && !message?.data?.HasPassed) {
            self.loading = false;
            self.$snotify.error(t('Invalid credit card credentials provided'));
          }

          if (message && message?.data?.HasPassed) {
            self.loading = true;
            await marketplaceApi.addCurrentUserPaymentDetail(
              self.paymetricAccessInfo.paymetricsIframeToken,
              self.address,
              self.newPaymentDetailIsDefault
            )
              .then(async() => {
                if (self.$route.query.subscription_redirect) {
                  self.$snotify.success(t('Payment method added, please update your subscription with the new payment method'));
                  await self.$router.push({path: self.$route.query.subscription_redirect});
                  return;
                }

                self.$snotify.success(t('Payment method added'));
                await self.$router.push({name: 'marketplace-account-payments'});
              })
              .catch(async e => {
                await self.getPaymetricAccessInfo();
                self.loading = false;
                handleApiErrors(e, self, 'Unable to add payment method');
              });
          }
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.personal-details--footer {
  border-radius: 0 0 1.2rem 1.2rem;
}
</style>
